import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { Calendar, Users, CreditCard, Check, AlertCircle } from 'lucide-react';
import { useBooking } from '../contexts/BookingContext';
import { addDays, format } from 'date-fns';
import toast from 'react-hot-toast';
import BookingLinks from '../components/BookingLinks';
import CalendarAvailability from '../components/CalendarAvailability';
import "react-datepicker/dist/react-datepicker.css";

interface BookingFormData {
  guestName: string;
  email: string;
  phone: string;
  specialRequests?: string;
  breakfast: boolean;
  airportPickup: boolean;
}

const Booking: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { createBooking, isDateAvailable, getBookingPrice } = useBooking();

  const parseDate = (d: any): Date | null => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  return isNaN(date.getTime()) ? null : date;
  };
  
  const [checkIn, setCheckIn] = useState<Date | null>(parseDate(location.state?.checkIn));
  const [checkOut, setCheckOut] = useState<Date | null>(parseDate(location.state?.checkOut));
  const [guests, setGuests] = useState(location.state?.guests || 2);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch } = useForm<BookingFormData>();
  
  const breakfast = watch('breakfast', false);
  const airportPickup = watch('airportPickup', false);

  const basePrice = checkIn && checkOut ? getBookingPrice(checkIn, checkOut, guests) : 0;
  const breakfastPrice = breakfast ? 25 * (checkIn && checkOut ? Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)) : 0) : 0;
  const airportPickupPrice = airportPickup ? 50 : 0;
  const totalPrice = basePrice + breakfastPrice + airportPickupPrice;


  



  const onSubmit = async (data: BookingFormData) => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      createBooking({
        ...data,
        checkIn,
        checkOut,
        guests,
        totalPrice,
        extras: {
          breakfast,
          airportPickup,
        }
      });

      toast.success('Booking confirmed! You will receive a confirmation email shortly.');
      navigate('/', { replace: true });
    } catch (error) {
      toast.error('Booking failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChange = () => {
    if (checkIn && checkOut) {
      // Check availability for selected dates
      const selectedDates = [];
      const currentDate = new Date(checkIn);
      while (currentDate < checkOut) {
        selectedDates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const unavailableDates = selectedDates.filter(date => !isDateAvailable(date));
      
      if (unavailableDates.length > 0) {
        toast.error('Some dates in your selection are not available');
        setCheckOut(null);
        return;
      }
    }
  };

  useEffect(() => {
    handleDateChange();
  }, [checkIn, checkOut]);

  return (
    <div className="min-h-screen bg-primary-50 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-earth-brown mb-4">
            Book Your Stay
          </h1>
          <p className="text-xl text-gray-600">
            Reserve your perfect getaway at Villa DINOOS
          </p>
        </motion.div>
        {/* External Booking Links */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <BookingLinks />
            </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            {/* <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-lg p-8 mb-8 mt-8"
            >
              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-serif font-semibold text-earth-brown mb-6">
                    Select Your Dates - Inquire Availability
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <Calendar className="w-4 h-4" />
                        <span>Check-in Date</span>
                      </label>
                      <DatePicker
                        selected={checkIn}
                        onChange={(date: Date | null) => setCheckIn(date)}
                        selectsStart
                        startDate={checkIn}
                        endDate={checkOut}
                        minDate={new Date()}
                        placeholderText="Select check-in date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <Calendar className="w-4 h-4" />
                        <span>Check-out Date</span>
                      </label>
                      <DatePicker
                        selected={checkOut}
                        onChange={(date: Date | null) => setCheckOut(date)}
                        selectsEnd
                        startDate={checkIn}
                        endDate={checkOut}
                        minDate={checkIn ? addDays(checkIn, 1) : addDays(new Date(), 1)}
                        placeholderText="Select check-out date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <Users className="w-4 h-4" />
                      <span>Number of Guests</span>
                    </label>
                    <select
                      value={guests}
                      onChange={(e) => setGuests(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    disabled={!checkIn || !checkOut}
                    className="w-full bg-earth-brown hover:bg-earth-brown/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-md font-semibold transition-colors"
                  >
                    Continue to Guest Details
                  </button>
                </div>
              )}

              {step === 2 && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif font-semibold text-earth-brown">
                      Guest Information
                    </h2>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-earth-brown hover:text-earth-brown/80 text-sm"
                    >
                      ← Back to Dates
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        {...register('guestName', { required: 'Full name is required' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                        placeholder="Enter your full name"
                      />
                      {errors.guestName && (
                        <p className="mt-1 text-sm text-red-600">{errors.guestName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        {...register('email', { 
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address'
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                        placeholder="Enter your email"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        {...register('phone', { required: 'Phone number is required' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Special Requests (Optional)
                      </label>
                      <textarea
                        {...register('specialRequests')}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-earth-brown focus:border-transparent"
                        placeholder="Any special requests or requirements..."
                      />
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-lg font-medium text-gray-900">Add-on Services</h3>
                      
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          {...register('breakfast')}
                          className="w-4 h-4 text-earth-brown border-gray-300 rounded focus:ring-earth-brown"
                        />
                        <span className="text-sm text-gray-700">
                          Daily breakfast service (+$25/night)
                        </span>
                      </label>

                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          {...register('airportPickup')}
                          className="w-4 h-4 text-earth-brown border-gray-300 rounded focus:ring-earth-brown"
                        />
                        <span className="text-sm text-gray-700">
                          Airport pickup service (+$50)
                        </span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-earth-brown hover:bg-earth-brown/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-md font-semibold transition-colors flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        <span>Confirm Inquiry</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div> */}

            

            {/* Real-time Calendar Availability
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <CalendarAvailability unavailableDates={[]} />
            </motion.div> */}
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-lg p-6 sticky top-24 mt-8"
            >
              {/* <h3 className="text-xl font-serif font-semibold text-earth-brown mb-4">
                Booking Summary
              </h3> */}

              {checkIn && checkOut ? (
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Check-in:</span>
                    <span className="font-medium">{format(checkIn, 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Check-out:</span>
                    <span className="font-medium">{format(checkOut, 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Guests:</span>
                    <span className="font-medium">{guests}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nights:</span>
                    <span className="font-medium">
                      {Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))}
                    </span>
                  </div>

                  <hr className="border-gray-200" />

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base rate:</span>
                      <span className="font-medium">${basePrice}</span>
                    </div>
                    {breakfast && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Breakfast:</span>
                        <span className="font-medium">${breakfastPrice}</span>
                      </div>
                    )}
                    {airportPickup && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Airport pickup:</span>
                        <span className="font-medium">${airportPickupPrice}</span>
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-200" />

                  <div className="flex justify-between text-lg font-semibold text-earth-brown">
                    <span>Total:</span>
                    <span>${totalPrice}</span>
                  </div>

                  <div className="mt-6 flex items-center space-x-2 text-sm text-gray-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>Free cancellation up to 24 hours before check-in</span>
                  </div>

                  {/* Eco-friendly note */}
                  <div className="mt-4 p-3 bg-olive-50 rounded-lg border border-olive-200">
                    <div className="flex items-center space-x-2 text-olive-700">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-sm">Eco-Friendly Stay</span>
                    </div>
                    <p className="text-xs text-olive-600 mt-1">
                      Your stay supports our sustainability initiatives including tourism in sri lanka and organic gardens.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  {/* <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" /> */}
                  {/* <p>Select your dates to see pricing</p> */}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;